import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, HostListener, ViewChild } from '@angular/core';
import { forkJoin } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { SimplePaginationModel } from 'src/app/models/pagination.model';
import { ResponseObject } from 'src/app/models/response-object.model';
import { DestinationService } from 'src/app/services/destination.service';
import { PlaceService } from 'src/app/services/place.service';
import { AssociationPlaceDestinationService } from 'src/app/services/association-place-destination.service';

@Component({
  selector: 'app-association-place-destination',
  templateUrl: './association-place-destination.component.html',
  styleUrls: ['./association-place-destination.component.scss']
})
export class AssociationPlaceDestinationComponent {
  loading = false;

  allPlaces: any[] = [];
  allDestinations: any[] = [];

  places: any[] = [];
  destinations: any[] = [];

  filterPlaces = '';
  filterDestinations = '';

  selectedPlaceId: number | null = null;
  selectedPlace: any = null;

  initialRelatedDestinationIdsBefore: number[] = [];
  currentRelatedDestinationIds: number[] = [];
  currentRelatedDestinations: any[] = [];

  paginationPlaces = new SimplePaginationModel(0, 5, 0, 0, 0);
  paginationDestinations = new SimplePaginationModel(0, 5, 0, 0, 0);

  @ViewChild('svgContainer', { static: true }) svgContainer: ElementRef;
  @HostListener('window:resize') onResize() { this.drawMultipleLinks(); }

  private timeoutPlaces: any = null;
  private timeoutDestinations: any = null;

  constructor(
    private toastService: ToastrService,
    private placeService: PlaceService,
    private destinationService: DestinationService,
    private associationService: AssociationPlaceDestinationService
  ) { }

  ngOnInit(): void {
    this.loadStartData();
  }


  loadStartData() {
    this.loadPlaces();
    this.loadDestinations();
  }

  loadPlaces() {
    this.placeService.getPlaces().subscribe({
      next: (resp: ResponseObject) => {
        this.allPlaces = resp.data || [];
        this.refreshPlacesPagination();
      },
      error: () => this.toastService.error('Error al obtener ubicaciones')
    });
  }

  loadDestinations() {
    this.destinationService.getDestinations().subscribe({
      next: (resp: ResponseObject) => {
        this.allDestinations = resp.data || [];
        this.refreshDestinationsPagination();
      },
      error: () => this.toastService.error('Error al obtener destinos')
    });
  }

  selectPlace(placeId: number) {
    if (this.selectedPlaceId === placeId) return;

    this.selectedPlaceId = placeId;
    this.selectedPlace = this.allPlaces.find(p => p.id === placeId);

    this.currentRelatedDestinationIds = [];
    this.currentRelatedDestinations = [];

    this.associationService.getAssociationsByPlace(placeId).subscribe({
      next: (resp: ResponseObject) => {
        const associations = resp.data || [];

        const relatedIds: number[] = associations.map((a: any) => a.destinationId);

        this.initialRelatedDestinationIdsBefore = [...relatedIds];
        this.currentRelatedDestinationIds = [...relatedIds];

        this.currentRelatedDestinations = this.allDestinations.filter(d =>
          relatedIds.includes(d.id)
        );

        this.refreshDestinationsPagination();
        this.drawMultipleLinks();
      },
      error: (error) => {
        console.error(error);
        this.toastService.error('Error al obtener asociaciones de la ubicación');
      }
    });
  }

  toggleDestination(d: any) {
    if (!this.selectedPlaceId) {
      this.toastService.warning('Selecciona primero una ubicación.');
      return;
    }

    const idx = this.currentRelatedDestinationIds.indexOf(d.id);
    if (idx === -1) {
      this.currentRelatedDestinationIds.push(d.id);
    } else {
      this.currentRelatedDestinationIds.splice(idx, 1);
    }

    this.currentRelatedDestinations = this.allDestinations.filter(dest =>
      this.currentRelatedDestinationIds.includes(dest.id)
    );

    this.refreshDestinationsPagination();
    this.drawMultipleLinks();
  }

  getDisabledSave(): boolean {
    const initialSet = new Set(this.initialRelatedDestinationIdsBefore);
    const currentSet = new Set(this.currentRelatedDestinationIds);

    const toAdd = this.currentRelatedDestinationIds.some(id => !initialSet.has(id));
    const toRemove = this.initialRelatedDestinationIdsBefore.some(id => !currentSet.has(id));

    return !(toAdd || toRemove) || !this.selectedPlaceId || this.loading;
  }


  saveAssociation() {
    if (!this.selectedPlaceId) return;

    const placeId = this.selectedPlaceId;

    const initialSet = new Set(this.initialRelatedDestinationIdsBefore);
    const currentSet = new Set(this.currentRelatedDestinationIds);

    const toAdd = this.currentRelatedDestinationIds.filter(id => !initialSet.has(id));
    const toRemove = this.initialRelatedDestinationIdsBefore.filter(id => !currentSet.has(id));

    if (toAdd.length === 0 && toRemove.length === 0) {
      this.toastService.info('No hay cambios por guardar.');
      return;
    }

    this.loading = true;

    const ops = [
      ...toAdd.map(did => this.associationService.createAssociation(placeId, did)),
      ...toRemove.map(did => this.associationService.deleteAssociation(placeId, did))
    ];

    forkJoin(ops).subscribe({
      next: () => {
        this.toastService.success('Asociaciones actualizadas correctamente', 'Éxito');
        this.loading = false;
        this.selectPlace(placeId);
      },
      error: (e: HttpErrorResponse) => {
        this.toastService.error(e?.error?.message || 'Error al actualizar asociaciones', 'Error');
        this.loading = false;
      }
    });
  }


  debouncedFilterPlaces() {
    clearTimeout(this.timeoutPlaces);
    this.timeoutPlaces = setTimeout(() => {
      this.paginationPlaces.page = 0;
      this.selectedPlaceId = null;
      this.refreshPlacesPagination();
      this.drawMultipleLinks();
    }, 250);
  }

  refreshPlacesPagination() {
    const q = (this.filterPlaces || '').toLowerCase();
    const filtered = this.allPlaces.filter(p =>
      (p.name || '').toLowerCase().includes(q)
    );

    this.paginationPlaces.total = filtered.length;
    this.paginationPlaces.start = this.paginationPlaces.page * this.paginationPlaces.size;
    const end = this.paginationPlaces.start + this.paginationPlaces.size;
    this.paginationPlaces.end = Math.min(end, filtered.length);

    this.places = filtered.slice(this.paginationPlaces.start, this.paginationPlaces.end);
    this.drawMultipleLinks();
  }

  debouncedFilterDestinations() {
    clearTimeout(this.timeoutDestinations);
    this.timeoutDestinations = setTimeout(() => {
      this.paginationDestinations.page = 0;
      this.refreshDestinationsPagination();
    }, 250);
  }

  refreshDestinationsPagination() {
    const q = (this.filterDestinations || '').toLowerCase();
    const matches = (d: any) =>
      (d.name || '').toLowerCase().includes(q) ||
      (d.code || '').toLowerCase().includes(q);

    const associatedSet = new Set(this.currentRelatedDestinationIds);
    const pinned = this.allDestinations.filter(d => associatedSet.has(d.id));
    const rest = this.allDestinations.filter(d => !associatedSet.has(d.id) && matches(d));

    const final = [...pinned, ...rest];

    this.paginationDestinations.total = final.length;
    this.paginationDestinations.start = this.paginationDestinations.page * this.paginationDestinations.size;
    const end = this.paginationDestinations.start + this.paginationDestinations.size;
    this.paginationDestinations.end = Math.min(end, final.length);

    this.destinations = final.slice(this.paginationDestinations.start, this.paginationDestinations.end);
    this.drawMultipleLinks();
  }

  drawMultipleLinks() {
    const svg = this.svgContainer?.nativeElement as SVGElement;
    if (!svg) return;

    svg.innerHTML = '';

    if (!this.selectedPlaceId || this.currentRelatedDestinationIds.length === 0) return;

    setTimeout(() => {
      const parentRect = (svg.parentElement as HTMLElement).getBoundingClientRect();
      const selectedRow = document.querySelector('#table-places tr.selected') as HTMLElement;
      if (!selectedRow) return;

      const selectedRect = selectedRow.getBoundingClientRect();
      const x1 = selectedRect.right - parentRect.left - 12;
      const y1 = selectedRect.top + selectedRect.height / 2 - parentRect.top;

      for (const did of this.currentRelatedDestinationIds) {
        const relatedRow = document.querySelector(`#table-destinations tr[data-id="${did}"]`) as HTMLElement;
        if (!relatedRow) continue;

        const rect = relatedRow.getBoundingClientRect();
        const x2 = rect.left - parentRect.left - 12;
        const y2 = rect.top + rect.height / 2 - parentRect.top;
        const xMiddle = x1 + ((x2 - x1) / 2);

        this.drawLine(svg, x1, xMiddle, y1, y1);
        this.drawLine(svg, xMiddle, x2, y2, y2);
        this.drawLine(svg, xMiddle, xMiddle, y1, y2);
      }
    }, 50);
  }

  private drawLine(svg: SVGElement, x1: number, x2: number, y1: number, y2: number) {
    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('x1', String(x1));
    line.setAttribute('y1', String(y1));
    line.setAttribute('x2', String(x2));
    line.setAttribute('y2', String(y2));
    line.setAttribute('stroke', '#14532d');
    line.setAttribute('stroke-width', '2');
    svg.appendChild(line);
  }
}
